import { useCallback, useEffect, useRef } from 'react';
import dayjs from 'dayjs';
import { AllPlanScheduleType, ChatSessionType, StreamChatSessionType } from '@demind-inc/core';
import { set } from 'lodash';

import { ChatJobContext, useSSEStreaming } from '../../../hooks/useSSEStreaming';
import { useChatJobRecovery } from '../../../hooks/useChatJobRecovery';
import { useAuthContext } from '../../AuthProvider';
import { ChatState } from '../types';
import {
  AssistantChatMessage,
  AssistantCommitAction,
  AssistantNavigation,
  ChatSuggestedEvent,
  CTAOption,
  EnrichmentPatchEvent,
  InsightSuggestion,
  UnifiedChatRichContextEntry,
} from '../../../types';
import { assistantChatFeedbackAtom, suggestAllAIPlanStateAtom } from '../../../recoil';
import { useSyncChatResult } from '../../../mutations';
import { useCalendarContext } from '../../CalendarProvider';
import { useFormattedResultHandler } from './useFormattedResultHandler';
import { metricsApi } from '../../../api';
import { usePreferences } from '../../../queries';
import { trackEventMixpanel, useFeatureFlag } from '../../../../utils';
import { streamingStore } from '../streamingStore';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { useMetricsContext } from '../../MetricsProvider';
import { useRoutineCache, useSchedulerRoutine } from '../../../queries/useSchedulerRoutine';
import { useTodoTasksCache } from '../../../queries/useTodoTasks';
import { collectPendingItemsForChat } from '../../../helpers/pendingItems.helpers';
import { ChatResultJson, parseChatStreamResult } from '../../../helpers/resultJson.schema';
import { processFinalChatResult, type PlanResultRuntime } from './planEntryResult.helpers';
import {
  discardPendingAllPlanSuggestionsByType,
  persistDiscardedPendingAllPlanSuggestionsByType,
  shouldDiscardCurrentAllPlanSuggestionsForDate,
} from '../../../../helpers/allPlanSuggestions.helpers';

interface UsePlanEntryHandlerParams {
  state: ChatState;
  updateState: (updates: Partial<ChatState>) => void;
  addMessage: (message: AssistantChatMessage) => void;
  updateMessage: (messageId: string, updates: Partial<AssistantChatMessage>) => void;
  addAbortController: (controller: AbortController) => void;
  setTemporalSuggestedEvents: (
    events: ChatSuggestedEvent[] | ((prevEvents: ChatSuggestedEvent[]) => ChatSuggestedEvent[])
  ) => void;
}

const upsertCommitAction = (
  actions: AssistantCommitAction[],
  incomingAction: AssistantCommitAction
): AssistantCommitAction[] => {
  const existingIndex = actions.findIndex((a) => a.action_id === incomingAction.action_id);
  if (existingIndex === -1) {
    return [...actions, incomingAction];
  }
  const next = [...actions];
  next[existingIndex] = incomingAction;
  return next;
};

export const usePlanEntryHandler = ({
  state,
  updateState,
  addMessage,
  updateMessage,
  addAbortController,
  setTemporalSuggestedEvents,
}: UsePlanEntryHandlerParams) => {
  const { user } = useAuthContext();
  const { featureFlags } = useFeatureFlag();
  const isV4Enabled = featureFlags.FF_lifestack_v4 ?? true;
  const { startStreaming, stopStreaming, isStreaming, error } = useSSEStreaming();
  const setFeedbackData = useSetRecoilState(assistantChatFeedbackAtom);
  const setSuggestAllAIPlanState = useSetRecoilState(suggestAllAIPlanStateAtom);
  const { syncChatResult } = useSyncChatResult();
  const { mainCalendar } = useCalendarContext();
  const { locationPreferences } = usePreferences({ preferenceId: user.preferenceId ?? '' });
  const { getMealOnDate } = useMetricsContext();
  // Warm the routine query so useRoutineCache().findInCache() can resolve
  // modify/remove diffs against the user's current routine activities.
  useSchedulerRoutine({ routineId: user?.routineId ?? '' });
  const { findInCache: findCurrentRoutineActivities } = useRoutineCache();
  const { findAllInCache: findCurrentTasks } = useTodoTasksCache();
  const allPlanState = useRecoilValue(suggestAllAIPlanStateAtom);

  const discardPendingAllPlanSuggestions = useCallback(
    async (scheduleTypes: AllPlanScheduleType[], targetDate?: string) => {
      if (!scheduleTypes.length) {
        return;
      }
      if (!shouldDiscardCurrentAllPlanSuggestionsForDate(targetDate)) {
        return;
      }
      setSuggestAllAIPlanState((prev) =>
        discardPendingAllPlanSuggestionsByType(prev, scheduleTypes)
      );
      await persistDiscardedPendingAllPlanSuggestionsByType(scheduleTypes, targetDate);
    },
    [setSuggestAllAIPlanState]
  );

  const buildPendingItems = useCallback(
    (sessionType: StreamChatSessionType | 'general') =>
      collectPendingItemsForChat({
        ...(sessionType === 'scheduler_chat' && {
          allPlanSchedules: allPlanState.schedules,
          allPlanAcceptedIds: allPlanState.acceptedIds,
          allPlanDiscardedIds: allPlanState.discardedIds,
        }),
        temporalSuggestedEvents: state.temporalSuggestedEvents,
        messages: state.messages,
      }),
    [
      allPlanState.schedules,
      allPlanState.acceptedIds,
      allPlanState.discardedIds,
      state.temporalSuggestedEvents,
      state.messages,
    ]
  );

  // Track current status texts array to avoid stale state when multiple texts arrive quickly
  const statusTextsRef = useRef<string[] | null>(null);
  // Preserve session context when a question is raised
  const sessionContextRef = useRef<{
    sessionType: StreamChatSessionType | 'general';
    date?: string;
    sessionId?: string;
  }>({ sessionType: 'general' });

  // Streaming data
  const streamingDataRef = useRef<{
    summaryText: string;
    streamingMessageId?: string;
    textId?: number;
  }>({ summaryText: '' });

  const activeSessionRef = useRef<string | null>(null);
  const hasReceivedStreamingChunkRef = useRef(false);
  // Dedupes SSE `question` + question-shaped `result_json` within one turn.
  const questionHandledFingerprintRef = useRef<string | null>(null);
  const messagesRef = useRef(state.messages);
  messagesRef.current = state.messages;
  const latestResultJsonRef = useRef<ChatResultJson | null>(null);
  const latestSuggestionsRef = useRef<InsightSuggestion[]>([]);
  // Tracks the most recent streaming message ID (persists past finalizeStreamingResponse)
  const lastStreamingMessageIdRef = useRef<string | undefined>(undefined);
  // Tracks the CTA message ID created by handleGeneralResult
  const ctaMessageIdRef = useRef<string | undefined>(undefined);
  // Tracks the current CTA suggested blocks for enrichment patch updates
  const latestCtaBlocksRef = useRef<ChatSuggestedEvent[]>([]);
  // result_json schedule suggestion already rendered in chat (enrichment patches).
  const inlineScheduleRef = useRef<{ messageId: string; date: string } | undefined>(undefined);
  // Persist across turns so a later reply that updates a pending item (matched
  // by action_id / eventId) syncs every earlier bubble + adds a fresh card.
  const scheduleMessageIdsByActionIdRef = useRef<Map<string, Set<string>>>(new Map());
  const scheduleBlocksByMessageIdRef = useRef<Map<string, ChatSuggestedEvent[]>>(new Map());
  const scheduleMessageSeqRef = useRef(0);
  const sleepMessageIdsByEventIdRef = useRef<Map<string, Set<string>>>(new Map());
  const sleepBlocksByMessageIdRef = useRef<Map<string, ChatSuggestedEvent[]>>(new Map());
  const mealMessageIdsByEventIdRef = useRef<Map<string, Set<string>>>(new Map());
  const mealBlocksByMessageIdRef = useRef<Map<string, ChatSuggestedEvent[]>>(new Map());
  const taskMessageIdsByEventIdRef = useRef<Map<string, Set<string>>>(new Map());
  const taskBlocksByMessageIdRef = useRef<Map<string, ChatSuggestedEvent[]>>(new Map());
  const routineMessageIdsByEventIdRef = useRef<Map<string, Set<string>>>(new Map());
  const routineBlocksByMessageIdRef = useRef<Map<string, ChatSuggestedEvent[]>>(new Map());
  const pendingCommitActionsRef = useRef<AssistantCommitAction[]>([]);
  const messageCommitActionsRef = useRef<Record<string, AssistantCommitAction[]>>({});
  // v4 only: status-only placeholder message id per sequence text_id
  const sequenceStatusMessageIdRef = useRef<Record<number, string>>({});
  // v4 only: message currently showing a live sequence.status line
  const liveSequenceStatusMessageIdRef = useRef<string | undefined>(undefined);
  const lastAssistantMessageIdRef = useRef<string | undefined>(undefined);
  const lastAssistantIsStatusOnlyRef = useRef(false);

  // Initialize the formatted result handler
  const { handleFormattedResult } = useFormattedResultHandler({
    state,
    updateState,
    addMessage,
    setTemporalSuggestedEvents,
    onCtaMessageCreated: (id) => {
      ctaMessageIdRef.current = id;
    },
    onCtaBlocksCreated: (blocks) => {
      latestCtaBlocksRef.current = blocks;
    },
  });

  // Sync ref with state when state changes
  useEffect(() => {
    statusTextsRef.current = state.loadingStatusText;
  }, [state.loadingStatusText]);

  const handleStatusText = useCallback(
    (statusText: string | null) => {
      if (statusText === null) {
        statusTextsRef.current = null;
        updateState({ loadingStatusText: [] });
      } else {
        const trimmed = statusText.trim().replace(/"/g, '');
        const currentTexts = statusTextsRef.current || [];
        if (currentTexts.length === 0 || currentTexts[currentTexts.length - 1] !== trimmed) {
          const newTexts = [...currentTexts, trimmed];
          statusTextsRef.current = newTexts;
          updateState({ loadingStatusText: newTexts });
        }
      }
    },
    [updateState]
  );

  const syncMetricsContextWithCurrentLocation = useCallback(async () => {
    const locationPref = (locationPreferences ?? {}) as { enabled?: boolean };
    const isLocationEnabled = Boolean(locationPref.enabled);

    if (!isLocationEnabled) {
      return;
    }

    if (!user?.metricId || !navigator.geolocation) {
      return;
    }

    try {
      const position = await new Promise<GeolocationPosition>((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: false,
          timeout: 8000,
          maximumAge: 60 * 1000,
        });
      });

      const capturedAt = new Date().toISOString();
      const date = dayjs().format('YYYY-MM-DD');

      await metricsApi.syncMetricsContext(user.metricId, {
        currentLocation: {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
          accuracy_m: position.coords.accuracy,
          captured_at: capturedAt,
          capturedAt,
          name: 'Current location',
          date,
        },
        date,
      });
    } catch (error) {
      console.warn('Unable to sync geolocation context for assistant chat:', error);
    }
  }, [locationPreferences, user?.metricId]);

  const handleQuestionEvent = useCallback(
    (questionData: unknown) => {
      statusTextsRef.current = null;
      updateState({ loading: false, loadingStatusText: [] });
      const questionDataObj = questionData as Record<string, unknown>;
      const questionSessionId = questionDataObj?.session_id ?? questionDataObj?.sessionId;
      if (typeof questionSessionId === 'string' && questionSessionId && sessionContextRef.current) {
        sessionContextRef.current = {
          ...sessionContextRef.current,
          sessionId: questionSessionId,
        };
      }

      const question = questionData as AssistantChatMessage['question'];
      const fingerprint = JSON.stringify({
        trigger: question?.trigger,
        context: question?.context,
        question: question?.question,
        options: question?.options?.map((option) => option.label),
      });

      // Same stream can emit both SSE `question` and a question-shaped
      // `result_json` — skip exact duplicates so ConversationQuestionCard
      // does not render twice.
      if (
        questionHandledFingerprintRef.current &&
        questionHandledFingerprintRef.current === fingerprint
      ) {
        return;
      }
      questionHandledFingerprintRef.current = fingerprint;

      // Supersede any prior unanswered question cards so conflicts don't stack
      // as multiple live CTAs (e.g. successive schedule-conflict questions).
      messagesRef.current.forEach((message) => {
        if (message.question && !message.isAnswered) {
          updateMessage(message.messageId, { isAnswered: true });
        }
      });

      addMessage({
        messageId: `question_${Date.now()}`,
        content: '',
        role: 'assistant',
        sentAt: new Date().toISOString(),
        question,
      });
    },
    [updateState, addMessage, updateMessage]
  );

  // ─── extractRichContextEntries ────────────────────────────────────────────

  const extractRichContextEntries = useCallback(
    (resultJson: unknown): UnifiedChatRichContextEntry[] => {
      const entries: UnifiedChatRichContextEntry[] = [];
      const result = resultJson as Record<string, unknown> | null;
      if (!result) return entries;

      // Shape 1: unified_chat_responses — rich_context may be in adjustments items or entry.query
      if (Array.isArray(result.unified_chat_responses)) {
        (result.unified_chat_responses as unknown[]).forEach((responseEntry, responseIdx) => {
          const entry = responseEntry as Record<string, unknown>;
          const date = typeof entry?.date === 'string' ? entry.date : undefined;
          const baseSuggestionId =
            typeof entry?.suggestion_id === 'string' && (entry.suggestion_id as string).trim()
              ? (entry.suggestion_id as string)
              : date || `response_${responseIdx}`;

          // 1a: Adjustment items — rich_context nested inside each item
          const adjustments = entry?.adjustments as Record<string, unknown> | undefined;
          if (adjustments && typeof adjustments === 'object') {
            const arrayAdjTypes = [
              'meals',
              'schedule',
              'todo_list',
              'routine_adjustments',
            ] as const;
            for (const adjType of arrayAdjTypes) {
              const items = adjustments[adjType];
              if (!Array.isArray(items)) continue;
              (items as unknown[]).forEach((item, itemIdx) => {
                const itemObj = item as Record<string, unknown>;
                if (!itemObj?.rich_context || typeof itemObj.rich_context !== 'object') return;
                entries.push({
                  date,
                  suggestionId: `${baseSuggestionId}_${adjType}_${itemIdx}`,
                  richContext: itemObj.rich_context as UnifiedChatRichContextEntry['richContext'],
                  source: 'adjustment',
                });
              });
            }

            // sleep is a single object, not an array
            const sleep = adjustments.sleep as Record<string, unknown> | undefined;
            if (sleep?.rich_context && typeof sleep.rich_context === 'object') {
              entries.push({
                date,
                suggestionId: `${baseSuggestionId}_sleep`,
                richContext: sleep.rich_context as UnifiedChatRichContextEntry['richContext'],
                source: 'adjustment',
              });
            }
          }

          // 1b: Query path inside entry
          const entryQuery = entry?.query as Record<string, unknown> | undefined;
          if (entryQuery?.rich_context && typeof entryQuery.rich_context === 'object') {
            entries.push({
              date,
              suggestionId: baseSuggestionId,
              richContext: entryQuery.rich_context as UnifiedChatRichContextEntry['richContext'],
              source: 'query',
            });
          }
        });
      }

      // Shape 2: Root-level query path (no unified_chat_responses)
      const rootQuery = result.query as Record<string, unknown> | undefined;
      if (rootQuery?.rich_context && typeof rootQuery.rich_context === 'object') {
        entries.push({
          suggestionId: 'flat',
          richContext: rootQuery.rich_context as UnifiedChatRichContextEntry['richContext'],
          source: 'query',
        });
      }

      return entries;
    },
    []
  );

  // ─── merge/dedupe rich contexts (unified + enrichment) ────────────────────

  const getRichContextContentType = useCallback((entry: UnifiedChatRichContextEntry): string => {
    const content = entry.richContext?.content as unknown as
      | { type?: string | null }
      | null
      | undefined;
    return typeof content?.type === 'string' ? content.type.trim().toLowerCase() : 'unknown';
  }, []);

  const getRichContextDedupeKey = useCallback(
    (entry: UnifiedChatRichContextEntry): string => {
      const content = entry.richContext?.content as unknown as
        | { type?: string | null; payload?: Record<string, unknown> | null }
        | null
        | undefined;

      const type = getRichContextContentType(entry);
      const payload = content?.payload && typeof content.payload === 'object' ? content.payload : null;

      // Enrichment patches can change workout titles (e.g. plan_title) — dedupe by date+type only.
      if (type === 'workout_plan') {
        return `${entry.date ?? ''}|${type}`;
      }

      const nestedMealContent =
        payload?.meal_content && typeof payload.meal_content === 'object'
          ? (payload.meal_content as Record<string, unknown>)
          : null;
      const discriminator =
        (payload &&
          (payload.suggestion_id ??
            payload.suggestionId ??
            payload.meal_id ??
            payload.id ??
            nestedMealContent?.suggestion_id ??
            nestedMealContent?.suggestionId ??
            nestedMealContent?.meal_id ??
            nestedMealContent?.id ??
            payload.meal_title ??
            payload.title ??
            payload.name ??
            nestedMealContent?.meal_title ??
            nestedMealContent?.title ??
            nestedMealContent?.name ??
            entry.suggestionId)) ||
        undefined;

      return `${entry.date ?? ''}|${type}|${
        typeof discriminator === 'string' || typeof discriminator === 'number'
          ? String(discriminator)
          : ''
      }`;
    },
    [getRichContextContentType]
  );

  const getPayloadScore = useCallback((entry: UnifiedChatRichContextEntry): number => {
    const content = entry.richContext?.content as unknown as
      | { payload?: Record<string, unknown> | null }
      | null
      | undefined;
    const payload = content?.payload && typeof content.payload === 'object' ? content.payload : null;
    const payloadKeys = payload ? Object.keys(payload).length : 0;
    const exerciseCount = Array.isArray(payload?.exercises) ? payload.exercises.length : 0;
    const pending = entry.richContext?.enrichment_pending ? 1 : 0;
    const hasStatus = typeof entry.richContext?.status_text === 'string' ? 1 : 0;
    const adjustmentBonus = entry.source === 'adjustment' ? 1 : 0;

    // Prefer non-pending, richer payloads; adjustment entries receive enrichment patches.
    return payloadKeys * 10 + exerciseCount * 5 + hasStatus + adjustmentBonus - pending * 100;
  }, []);

  const mergeAndPartitionRichContexts = useCallback(
    (
      queryEntries: UnifiedChatRichContextEntry[],
      adjustmentEntries: UnifiedChatRichContextEntry[]
    ): { mergedQueryEntries: UnifiedChatRichContextEntry[]; remainingAdjustmentEntries: UnifiedChatRichContextEntry[] } => {
      const bestByKey = new Map<string, UnifiedChatRichContextEntry>();
      const keyOwner = new Map<string, 'query' | 'adjustment'>();

      const consider = (entry: UnifiedChatRichContextEntry, owner: 'query' | 'adjustment') => {
        const key = getRichContextDedupeKey(entry);
        const existing = bestByKey.get(key);
        if (!existing) {
          bestByKey.set(key, entry);
          keyOwner.set(key, owner);
          return;
        }

        const existingScore = getPayloadScore(existing);
        const nextScore = getPayloadScore(entry);
        if (nextScore > existingScore) {
          bestByKey.set(key, entry);
          keyOwner.set(key, owner);
        }
      };

      queryEntries.forEach((e) => consider(e, 'query'));
      adjustmentEntries.forEach((e) => consider(e, 'adjustment'));

      const mergedQueryEntries: UnifiedChatRichContextEntry[] = [];
      const remainingAdjustmentEntries: UnifiedChatRichContextEntry[] = [];

      // Keep original relative order as much as possible.
      const queryKeys = queryEntries.map(getRichContextDedupeKey);
      const adjustmentKeys = adjustmentEntries.map(getRichContextDedupeKey);
      const queryKeySet = new Set(queryKeys);
      const emittedQueryKeys = new Set<string>();
      const emittedAdjustmentKeys = new Set<string>();

      for (const key of queryKeys) {
        if (emittedQueryKeys.has(key)) continue;
        emittedQueryKeys.add(key);

        const best = bestByKey.get(key);
        if (!best) continue;
        // If the best version lives on the adjustment side, still render it with the main response.
        mergedQueryEntries.push({ ...best, source: 'query' });
      }

      for (const key of adjustmentKeys) {
        if (emittedAdjustmentKeys.has(key)) continue;
        emittedAdjustmentKeys.add(key);

        const best = bestByKey.get(key);
        if (!best) continue;
        // Only keep adjustment entries that don't overlap with what we already show in the main response.
        if (queryKeySet.has(key)) continue;
        remainingAdjustmentEntries.push(best);
      }

      return { mergedQueryEntries, remainingAdjustmentEntries };
    },
    [getPayloadScore, getRichContextDedupeKey]
  );

  // ─── normalizeInsightSuggestions ─────────────────────────────────────────

  const normalizeInsightSuggestions = useCallback(
    (rawSuggestions: unknown): InsightSuggestion[] => {
      if (!Array.isArray(rawSuggestions)) return [];
      return rawSuggestions
        .map((item, index): InsightSuggestion | null => {
          if (!item || typeof item !== 'object') return null;
          const raw = item as Record<string, unknown>;
          const title = typeof raw.title === 'string' ? raw.title.trim() : '';
          const description = typeof raw.description === 'string' ? raw.description.trim() : '';
          if (!title || !description) return null;
          return {
            id:
              typeof raw.id === 'string' && (raw.id as string).trim()
                ? (raw.id as string)
                : `insight_${Date.now()}_${index}`,
            title,
            description,
            signal_source:
              typeof raw.signal_source === 'string' && (raw.signal_source as string).trim()
                ? (raw.signal_source as string)
                : 'combined',
            urgency:
              typeof raw.urgency === 'string' && (raw.urgency as string).trim()
                ? (raw.urgency as string)
                : 'soon',
            expires_at: typeof raw.expires_at === 'string' ? raw.expires_at : undefined,
            cta_action:
              raw.cta_action && typeof raw.cta_action === 'object'
                ? {
                    action_type:
                      typeof (raw.cta_action as Record<string, unknown>).action_type === 'string'
                        ? String((raw.cta_action as Record<string, unknown>).action_type)
                        : 'none',
                    payload:
                      (raw.cta_action as Record<string, unknown>).payload &&
                      typeof (raw.cta_action as Record<string, unknown>).payload === 'object'
                        ? ((raw.cta_action as Record<string, unknown>).payload as Record<
                            string,
                            unknown
                          >)
                        : undefined,
                  }
                : undefined,
          };
        })
        .filter(Boolean)
        .slice(0, 3) as InsightSuggestion[];
    },
    []
  );

  // ─── Streaming delta ──────────────────────────────────────────────────────

  const handleStreamingDelta = useCallback(
    (deltaText: string, sessionType: StreamChatSessionType | 'general', textId = 0) => {
      hasReceivedStreamingChunkRef.current = true;

      // A text_id change (v4 only; always 0 on v2/v3) marks a new bubble: freeze the
      // previous bubble's content, then start a fresh message for the new id.
      if (
        isV4Enabled &&
        streamingDataRef.current.streamingMessageId &&
        textId !== streamingDataRef.current.textId
      ) {
        const prevId = streamingDataRef.current.streamingMessageId;
        updateMessage(prevId, {
          content: streamingDataRef.current.summaryText,
        });
        streamingStore.end();
        streamingDataRef.current.streamingMessageId = undefined;
        streamingDataRef.current.summaryText = '';
      }

      streamingDataRef.current.summaryText += deltaText;
      streamingDataRef.current.textId = textId;

      // If a streaming message is already active for this bubble, just push to the store
      if (streamingDataRef.current.streamingMessageId) {
        streamingStore.append(deltaText);
        return;
      }

      // Hide loader when first chunk arrives
      statusTextsRef.current = null;
      updateState({ loading: false, loadingStatusText: [] });

      const streamingMessageId = `streaming_${sessionType}_${textId}_${Date.now()}`;
      streamingDataRef.current.streamingMessageId = streamingMessageId;
      lastStreamingMessageIdRef.current = streamingMessageId;
      lastAssistantMessageIdRef.current = streamingMessageId;
      lastAssistantIsStatusOnlyRef.current = false;
      statusTextsRef.current = null;

      const pendingCommitActions = pendingCommitActionsRef.current;
      if (pendingCommitActions.length > 0) {
        messageCommitActionsRef.current[streamingMessageId] = pendingCommitActions;
        pendingCommitActionsRef.current = [];
      }

      const streamingMessage: AssistantChatMessage = {
        messageId: streamingMessageId,
        content: '', // UI reads from external store during streaming
        role: 'assistant',
        sentAt: new Date().toISOString(),
        commitActions: pendingCommitActions.length > 0 ? pendingCommitActions : undefined,
      };

      // Activate external store with accumulated content so far
      streamingStore.setActive(streamingMessageId, streamingDataRef.current.summaryText);
      addMessage(streamingMessage);
    },
    [addMessage, isV4Enabled, updateMessage, updateState]
  );

  // v4 only: a 'sequence' bubble that arrives complete (no per-token deltas).
  const handleWholeMessage = useCallback(
    (message: string, sessionType: StreamChatSessionType | 'general', textId: number) => {
      hasReceivedStreamingChunkRef.current = true;
      statusTextsRef.current = null;
      updateState({ loading: false, loadingStatusText: [] });

      const pendingCommitActions = pendingCommitActionsRef.current;
      const liveStatusId = liveSequenceStatusMessageIdRef.current;
      liveSequenceStatusMessageIdRef.current = undefined;
      const placeholderId = sequenceStatusMessageIdRef.current[textId];
      if (placeholderId) {
        delete sequenceStatusMessageIdRef.current[textId];
      }

      const reusableId =
        placeholderId ??
        (lastAssistantIsStatusOnlyRef.current ? lastAssistantMessageIdRef.current : undefined);

      if (reusableId) {
        if (pendingCommitActions.length > 0) {
          messageCommitActionsRef.current[reusableId] = pendingCommitActions;
          pendingCommitActionsRef.current = [];
        }
        updateMessage(reusableId, {
          content: message,
          statusText: undefined,
          commitActions: pendingCommitActions.length > 0 ? pendingCommitActions : undefined,
        });
        lastAssistantMessageIdRef.current = reusableId;
        lastAssistantIsStatusOnlyRef.current = false;
        lastStreamingMessageIdRef.current = reusableId;
        return;
      }

      if (liveStatusId) {
        updateMessage(liveStatusId, { statusText: undefined });
      }

      const messageId = `sequence_${sessionType}_${textId}_${Date.now()}`;
      if (pendingCommitActions.length > 0) {
        messageCommitActionsRef.current[messageId] = pendingCommitActions;
        pendingCommitActionsRef.current = [];
      }

      lastAssistantMessageIdRef.current = messageId;
      lastAssistantIsStatusOnlyRef.current = false;
      lastStreamingMessageIdRef.current = messageId;

      addMessage({
        messageId,
        content: message,
        role: 'assistant',
        sentAt: new Date().toISOString(),
        commitActions: pendingCommitActions.length > 0 ? pendingCommitActions : undefined,
      });
    },
    [addMessage, updateMessage, updateState]
  );

  // v4 only: sequence.status is a transient working line under the last bubble.
  const handleSequenceStatus = useCallback(
    (status: string, textId: number) => {
      const trimmedStatus = status.trim().replace(/"/g, '');
      if (!trimmedStatus) {
        return;
      }
      hasReceivedStreamingChunkRef.current = true;
      updateState({ loading: false });

      const existingPlaceholderId = sequenceStatusMessageIdRef.current[textId];
      if (existingPlaceholderId) {
        updateMessage(existingPlaceholderId, { statusText: trimmedStatus });
        liveSequenceStatusMessageIdRef.current = existingPlaceholderId;
        return;
      }

      const lastId = lastAssistantMessageIdRef.current;
      if (lastId && !lastAssistantIsStatusOnlyRef.current) {
        updateMessage(lastId, { statusText: trimmedStatus });
        liveSequenceStatusMessageIdRef.current = lastId;
        return;
      }

      if (lastId && lastAssistantIsStatusOnlyRef.current) {
        updateMessage(lastId, { statusText: trimmedStatus });
        sequenceStatusMessageIdRef.current[textId] = lastId;
        liveSequenceStatusMessageIdRef.current = lastId;
        return;
      }

      const messageId = `sequence_status_${textId}_${Date.now()}`;
      sequenceStatusMessageIdRef.current[textId] = messageId;
      liveSequenceStatusMessageIdRef.current = messageId;
      lastAssistantMessageIdRef.current = messageId;
      lastAssistantIsStatusOnlyRef.current = true;
      addMessage({
        messageId,
        content: '',
        role: 'assistant',
        sentAt: new Date().toISOString(),
        statusText: trimmedStatus,
      });
    },
    [addMessage, updateMessage, updateState]
  );

  // ─── Commit action ────────────────────────────────────────────────────────

  const appendCommitActionToMessage = useCallback(
    (messageId: string, action: AssistantCommitAction) => {
      const existingCommitActions =
        messageCommitActionsRef.current[messageId] ??
        state.messages.find((m) => m.messageId === messageId)?.commitActions ??
        [];
      const nextCommitActions = upsertCommitAction(existingCommitActions, action);
      messageCommitActionsRef.current[messageId] = nextCommitActions;
      updateMessage(messageId, { commitActions: nextCommitActions });
    },
    [state.messages, updateMessage]
  );

  const handleCommitActionEvent = useCallback(
    (commitActionData: AssistantCommitAction) => {
      if (!commitActionData || typeof commitActionData !== 'object') return;

      const activeStreamingMessageId = streamingDataRef.current.streamingMessageId;
      if (activeStreamingMessageId) {
        appendCommitActionToMessage(activeStreamingMessageId, commitActionData);
        return;
      }

      if (!hasReceivedStreamingChunkRef.current) {
        pendingCommitActionsRef.current = upsertCommitAction(
          pendingCommitActionsRef.current,
          commitActionData
        );
        return;
      }

      if (lastStreamingMessageIdRef.current) {
        appendCommitActionToMessage(lastStreamingMessageIdRef.current, commitActionData);
        return;
      }

      const commitActionMessageId = `commit_action_${Date.now()}`;
      messageCommitActionsRef.current[commitActionMessageId] = [commitActionData];
      addMessage({
        messageId: commitActionMessageId,
        content: '',
        role: 'assistant',
        sentAt: new Date().toISOString(),
        commitActions: [commitActionData],
      });
    },
    [addMessage, appendCommitActionToMessage]
  );

  const handleNavigateEvent = useCallback(
    (navigation: AssistantNavigation) => {
      addMessage({
        messageId: `navigate_${Date.now()}`,
        content: '',
        role: 'assistant',
        sentAt: new Date().toISOString(),
        navigation,
      });
    },
    [addMessage]
  );

  // ─── Enrichment patch ────────────────────────────────────────────────────

  const handleEnrichmentPatchEvent = useCallback(
    (patchData: EnrichmentPatchEvent) => {
      if (!patchData || typeof patchData !== 'object') return;

      const fieldPath = typeof patchData.field === 'string' ? patchData.field : '';
      if (!fieldPath) return;

      const currentResult = latestResultJsonRef.current as Record<string, unknown> | null;
      if (!currentResult) return;

      const suggestionId =
        typeof patchData.suggestion_id === 'string' ? patchData.suggestion_id : undefined;

      const patchValue = patchData.value;

      const setStatusTextOnRichContextPath = (richContextPath: string) => {
        if (!richContextPath) return;
        if (typeof patchData.status_text === 'string') {
          set(currentResult, `${richContextPath}.status_text`, patchData.status_text);
        }
      };

      const clearEnrichmentPendingIfPayloadPresent = (richContextPath: string) => {
        if (
          !richContextPath ||
          !patchValue ||
          typeof patchValue !== 'object' ||
          Array.isArray(patchValue)
        ) {
          return;
        }
        const patchPayload = patchValue as Record<string, unknown>;
        if (Object.keys(patchPayload).length > 0) {
          set(currentResult, `${richContextPath}.enrichment_pending`, false);
        }
      };

      const maybeInferContentType = (target: Record<string, unknown>, contentTypePath: string) => {
        if (
          !patchValue ||
          typeof patchValue !== 'object' ||
          patchValue === null ||
          Array.isArray(patchValue)
        ) {
          return;
        }
        const patchPayload = patchValue as Record<string, unknown>;
        if ('dish_name' in patchPayload) {
          set(target, contentTypePath, 'meal_suggestion');
        } else if (
          'duration_h' in patchPayload ||
          'quality_score' in patchPayload ||
          'efficiency' in patchPayload ||
          'sleep_time' in patchPayload ||
          'sleep_summary' in patchPayload
        ) {
          set(target, contentTypePath, 'sleep_summary');
        } else if (
          'workout_type' in patchPayload ||
          'exercises' in patchPayload ||
          'plan_title' in patchPayload
        ) {
          set(target, contentTypePath, 'workout_plan');
        }
      };

      // Absolute paths starting with unified_chat_responses.{i}.*
      if (fieldPath.startsWith('unified_chat_responses.')) {
        set(currentResult, fieldPath, patchValue);

        if (fieldPath.includes('rich_context') && fieldPath.endsWith('.content.payload')) {
          const contentTypePath = fieldPath.slice(0, -'.payload'.length) + '.type';
          maybeInferContentType(currentResult, contentTypePath);
        }

        if (fieldPath.includes('rich_context')) {
          const richContextPath = fieldPath.slice(
            0,
            fieldPath.indexOf('rich_context') + 'rich_context'.length
          );
          setStatusTextOnRichContextPath(richContextPath);
          if (fieldPath.endsWith('.content.payload')) {
            clearEnrichmentPendingIfPayloadPresent(richContextPath);
          }
        }
      } else if (fieldPath.startsWith('query.rich_context')) {
        const canPatchResponseEntry =
          suggestionId &&
          suggestionId !== 'flat' &&
          Array.isArray(currentResult.unified_chat_responses);

        if (canPatchResponseEntry) {
          const responseIndex = (currentResult.unified_chat_responses as unknown[]).findIndex(
            (entry, index) => {
              const e = entry as Record<string, unknown>;
              const entrySuggestionId =
                typeof e?.suggestion_id === 'string' && (e.suggestion_id as string).trim()
                  ? (e.suggestion_id as string)
                  : e?.date || `response_${index}`;
              return entrySuggestionId === suggestionId;
            }
          );

          if (responseIndex >= 0) {
            set(
              (currentResult.unified_chat_responses as unknown[])[responseIndex] as Record<
                string,
                unknown
              >,
              fieldPath,
              patchValue
            );
            if (typeof patchData.status_text === 'string') {
              set(
                (currentResult.unified_chat_responses as unknown[])[responseIndex] as Record<
                  string,
                  unknown
                >,
                'query.rich_context.status_text',
                patchData.status_text
              );
            }
          } else {
            set(currentResult, fieldPath, patchValue);
            if (
              typeof patchData.status_text === 'string' &&
              (currentResult?.query as Record<string, unknown> | undefined)?.rich_context
            ) {
              set(currentResult, 'query.rich_context.status_text', patchData.status_text);
            }
          }
        } else {
          set(currentResult, fieldPath, patchValue);
          if (
            typeof patchData.status_text === 'string' &&
            (currentResult?.query as Record<string, unknown> | undefined)?.rich_context
          ) {
            set(currentResult, 'query.rich_context.status_text', patchData.status_text);
          }
        }
      } else {
        // Relative path: patch against unified_chat_responses entry
        if (!Array.isArray(currentResult.unified_chat_responses)) return;

        const responseIndex = (currentResult.unified_chat_responses as unknown[]).findIndex(
          (entry, index) => {
            const e = entry as Record<string, unknown>;
            const entrySuggestionId =
              typeof e?.suggestion_id === 'string' && (e.suggestion_id as string).trim()
                ? (e.suggestion_id as string)
                : e?.date || `response_${index}`;
            return suggestionId ? entrySuggestionId === suggestionId : index === 0;
          }
        );
        if (responseIndex < 0) return;

        const responseEntry = (currentResult.unified_chat_responses as unknown[])[
          responseIndex
        ] as Record<string, unknown>;
        set(responseEntry, fieldPath, patchValue);

        if (
          fieldPath.includes('rich_context') &&
          fieldPath.endsWith('.content.payload') &&
          typeof patchValue === 'object' &&
          patchValue !== null &&
          !Array.isArray(patchValue)
        ) {
          const contentTypePath = fieldPath.slice(0, -'.payload'.length) + '.type';
          maybeInferContentType(responseEntry, contentTypePath);

          const richContextPath = fieldPath.slice(
            0,
            fieldPath.indexOf('rich_context') + 'rich_context'.length
          );
          if (richContextPath) {
            const hasMeaningfulPayload =
              Object.keys(patchValue as Record<string, unknown>).length > 0;
            if (hasMeaningfulPayload) {
              set(responseEntry, `${richContextPath}.enrichment_pending`, false);
            }
          }
        }

        if (typeof patchData.status_text === 'string') {
          if (
            (responseEntry?.query as Record<string, unknown> | undefined)?.rich_context &&
            fieldPath.startsWith('query.rich_context')
          ) {
            set(responseEntry, 'query.rich_context.status_text', patchData.status_text);
          } else if (fieldPath.includes('rich_context')) {
            const richContextPath = fieldPath.slice(
              0,
              fieldPath.indexOf('rich_context') + 'rich_context'.length
            );
            if (richContextPath) {
              set(responseEntry, `${richContextPath}.status_text`, patchData.status_text);
            }
          }
        }
      }

      const richContextEntries = extractRichContextEntries(currentResult);
      const queryEntries = richContextEntries.filter((e) => e.source === 'query');
      const adjustmentEntries = richContextEntries.filter((e) => e.source === 'adjustment');
      const { mergedQueryEntries, remainingAdjustmentEntries } = mergeAndPartitionRichContexts(
        queryEntries,
        adjustmentEntries
      );

      // Update the streaming message with query richContexts
      const messageId = lastStreamingMessageIdRef.current;
      if (messageId) {
        updateMessage(messageId, {
          insightSuggestions:
            latestSuggestionsRef.current.length > 0 ? latestSuggestionsRef.current : undefined,
          richContexts: mergedQueryEntries.length > 0 ? mergedQueryEntries : undefined,
        });
      }

      // Update the CTA message with adjustment richContexts (clear when merged into query)
      const ctaMessageId = ctaMessageIdRef.current;
      if (ctaMessageId) {
        updateMessage(ctaMessageId, {
          richContexts:
            remainingAdjustmentEntries.length > 0 ? remainingAdjustmentEntries : undefined,
        });
      }

      // Handle adjustments.meals.{i}.content enrichment patches → update CTA block mealContent
      const mealContentMatch = fieldPath.match(/^adjustments\.meals\.(\d+)\.content$/);
      if (
        mealContentMatch &&
        ctaMessageId &&
        patchValue &&
        typeof patchValue === 'object' &&
        !Array.isArray(patchValue)
      ) {
        const mealIdx = parseInt(mealContentMatch[1], 10);
        let mealBlockCount = 0;
        const updatedBlocks = latestCtaBlocksRef.current.map((block) => {
          if (block.autoScheduleFrom?.from === 'meal') {
            if (mealBlockCount === mealIdx) {
              mealBlockCount++;
              return { ...block, mealContent: patchValue as ChatSuggestedEvent['mealContent'] };
            }
            mealBlockCount++;
          }
          return block;
        });
        latestCtaBlocksRef.current = updatedBlocks;
        updateMessage(ctaMessageId, { suggestedBlocks: updatedBlocks });
      }
    },
    [extractRichContextEntries, mergeAndPartitionRichContexts, updateMessage]
  );

  // ─── Final result ─────────────────────────────────────────────────────────

  const handleFinalResult = useCallback(
    async (
      result: unknown,
      sessionType: StreamChatSessionType,
      date?: string,
      sessionId?: string,
      prompt?: string
    ) => {
      let parsedResult = result;
      if (typeof result === 'string') {
        try {
          parsedResult = JSON.parse(result);
        } catch {
          parsedResult = result;
        }
      }

      const parsedResultObj = parsedResult as Record<string, unknown>;

      // Prefer calendar's parseChatStreamResult when possible; fall back to
      // the legacy cast so a soft schema miss still renders cards.
      let resultJson: ChatResultJson;
      let sessionIdFromPayload: string | undefined =
        typeof parsedResultObj?.session_id === 'string'
          ? (parsedResultObj.session_id as string)
          : undefined;
      try {
        const parsed = parseChatStreamResult(result);
        if (parsed.kind === 'question') {
          // Same path as SSE `question` — handleQuestionEvent dedupes if both fire.
          handleQuestionEvent(parsed.question);
          return;
        }
        resultJson = parsed.resultJson;
        sessionIdFromPayload = parsed.sessionId ?? sessionIdFromPayload;
      } catch {
        // Legacy fallback: question-shaped payloads that fail schema parse.
        if (parsedResultObj?.options && parsedResultObj?.trigger) {
          handleQuestionEvent(parsedResult);
          return;
        }
        resultJson = (parsedResultObj?.result_json ?? parsedResult) as ChatResultJson;
      }

      await processFinalChatResult({
        resultJson,
        originalResult: result,
        sessionIdFromPayload,
        sessionType,
        date,
        sessionId,
        prompt,
        stateSessionId: state.chatSessionId,
        runtime: {
          isV4Enabled,
          user,
          calendarId: mainCalendar?.calendarId,
          addMessage,
          updateMessage,
          updateState,
          setTemporalSuggestedEvents,
          setSuggestAllAIPlanState,
          setFeedbackData,
          getMealOnDate,
          findCurrentTasks,
          findCurrentRoutineActivities,
          syncChatResult: async (params) =>
            syncChatResult({
              sessionId: params.sessionId as string,
              sessionType: params.sessionType,
              routineId: params.routineId!,
              resultJson: params.resultJson,
              userId: params.userId,
              metricId: params.metricId,
              calendarId: params.calendarId,
              dateToBeSuggested: params.dateToBeSuggested,
            }),
          handleFormattedResult: handleFormattedResult as PlanResultRuntime['handleFormattedResult'],
          discardPendingAllPlanSuggestions,
          streamingDataRef,
          latestResultJsonRef,
          latestSuggestionsRef,
          inlineScheduleRef,
          scheduleMessageSeqRef,
          scheduleMessageIdsByActionIdRef,
          scheduleBlocksByMessageIdRef,
          sleepMessageIdsByEventIdRef,
          sleepBlocksByMessageIdRef,
          mealMessageIdsByEventIdRef,
          mealBlocksByMessageIdRef,
          taskMessageIdsByEventIdRef,
          taskBlocksByMessageIdRef,
          routineMessageIdsByEventIdRef,
          routineBlocksByMessageIdRef,
        },
      });

      statusTextsRef.current = null;
      updateState({ loadingStatusText: [] });
    },
    [
      state.chatSessionId,
      user,
      mainCalendar,
      isV4Enabled,
      syncChatResult,
      handleFormattedResult,
      setSuggestAllAIPlanState,
      setFeedbackData,
      setTemporalSuggestedEvents,
      updateState,
      addMessage,
      updateMessage,
      getMealOnDate,
      findCurrentTasks,
      findCurrentRoutineActivities,
      discardPendingAllPlanSuggestions,
      handleQuestionEvent,
    ]
  );

  // ─── Finalize streaming ───────────────────────────────────────────────────

  const finalizeStreamingResponse = useCallback(async () => {
    const id = streamingDataRef.current.streamingMessageId;
    if (id) {
      updateMessage(id, { content: streamingDataRef.current.summaryText });
    } else if (pendingCommitActionsRef.current.length > 0) {
      const commitActionMessageId = `commit_action_${Date.now()}`;
      const pendingCommitActions = pendingCommitActionsRef.current;
      messageCommitActionsRef.current[commitActionMessageId] = pendingCommitActions;
      addMessage({
        messageId: commitActionMessageId,
        content: '',
        role: 'assistant',
        sentAt: new Date().toISOString(),
        commitActions: pendingCommitActions,
      });
    }
    pendingCommitActionsRef.current = [];
    streamingStore.clear();

    if (streamingDataRef.current.streamingMessageId) {
      streamingDataRef.current.streamingMessageId = undefined;
    }

    activeSessionRef.current = null;
    hasReceivedStreamingChunkRef.current = false;
    statusTextsRef.current = null;
    updateState({ loading: false, loadingStatusText: [] });
  }, [addMessage, updateMessage, updateState]);

  // ─── General chat ─────────────────────────────────────────────────────────

  const handleGeneralChat = useCallback(
    async (
      userMessage: string,
      options?: { previousAssistantMessage?: string; signal?: AbortSignal }
    ) => {
      const abortController = new AbortController();
      addAbortController(abortController);

      try {
        statusTextsRef.current = [];
        updateState({ loading: true, loadingStatusText: [] });

        const sessionId = state.chatSessionId ?? undefined;

        streamingDataRef.current = { summaryText: '', streamingMessageId: undefined, textId: undefined };
        sessionContextRef.current = { sessionType: 'general', sessionId };
        hasReceivedStreamingChunkRef.current = false;
        questionHandledFingerprintRef.current = null;
        ctaMessageIdRef.current = undefined;
        inlineScheduleRef.current = undefined;
        latestCtaBlocksRef.current = [];
        pendingCommitActionsRef.current = [];
        sequenceStatusMessageIdRef.current = {};
        liveSequenceStatusMessageIdRef.current = undefined;
        lastAssistantMessageIdRef.current = undefined;
        lastAssistantIsStatusOnlyRef.current = false;

        void syncMetricsContextWithCurrentLocation();

        startStreaming({
          sessionId,
          prompt: userMessage,
          sessionType: 'general',
          pendingItems: buildPendingItems('general'),
          onMessage: (deltaText, textId) => {
            handleStreamingDelta(deltaText, 'general', textId);
          },
          onWholeMessage: (message, textId) => {
            handleWholeMessage(message, 'general', textId);
          },
          onSequenceStatus: handleSequenceStatus,
          onError: () => {
            addMessage({
              messageId: String(state.messages.length + 2),
              content: 'Sorry, I encountered an error during streaming. Please try again.',
              role: 'assistant',
              sentAt: new Date().toISOString(),
            });
            updateState({ loading: false, loadingStatusText: [] });
            activeSessionRef.current = null;
          },
          onStreamEnd: () => {
            finalizeStreamingResponse();
            activeSessionRef.current = null;
          },
          onStatusText: handleStatusText,
          onQuestion: handleQuestionEvent,
          onCommitAction: handleCommitActionEvent,
          onNavigate: handleNavigateEvent,
          onEnrichmentPatch: handleEnrichmentPatchEvent,
          onResultOut: (result) => {
            handleFinalResult(result, 'general', undefined, sessionId, userMessage);
          },
        });

        setFeedbackData((prev) => ({
          ...prev,
          scienceApiReq: null,
          scienceApiRes: null,
          currentTime: dayjs().toISOString(),
          sessionType: 'general',
          prompt: userMessage,
        }));

        const eventPayload: { message: string; previousAssistantMessage?: string } = {
          message: userMessage.trim(),
        };

        if (options?.previousAssistantMessage) {
          eventPayload.previousAssistantMessage = options.previousAssistantMessage.trim();
        }

        trackEventMixpanel('assistant_chat_message_sent', eventPayload);

        return '';
      } catch (err) {
        if (err instanceof Error && err.name === 'CanceledError') return;
        throw err;
      }
    },
    [
      user,
      updateState,
      addAbortController,
      startStreaming,
      addMessage,
      handleStatusText,
      handleStreamingDelta,
      handleWholeMessage,
      handleSequenceStatus,
      finalizeStreamingResponse,
      handleQuestionEvent,
      handleCommitActionEvent,
      handleNavigateEvent,
      handleEnrichmentPatchEvent,
      handleFinalResult,
      buildPendingItems,
      syncMetricsContextWithCurrentLocation,
      state.messages.length,
      state.chatSessionId,
    ]
  );

  // ─── Plan chat ────────────────────────────────────────────────────────────

  const handlePlanChat = useCallback(
    async (
      sessionType: StreamChatSessionType,
      userMessage: string,
      targetDate?: string,
      signal?: AbortSignal
    ) => {
      const abortController = new AbortController();
      addAbortController(abortController);

      try {
        const date = targetDate || dayjs().format('YYYY-MM-DD');
        const sessionId = state.chatSessionId ?? undefined;

        statusTextsRef.current = [];
        updateState({ loading: true, loadingStatusText: [] });

        setFeedbackData({
          scienceApiReq: null,
          scienceApiRes: null,
          currentTime: dayjs().toISOString(),
          sessionType: sessionType,
          sessionId: sessionId,
          prompt: userMessage,
        });

        streamingDataRef.current = { summaryText: '', streamingMessageId: undefined, textId: undefined };
        sessionContextRef.current = { sessionType, date, sessionId };
        hasReceivedStreamingChunkRef.current = false;
        questionHandledFingerprintRef.current = null;
        ctaMessageIdRef.current = undefined;
        inlineScheduleRef.current = undefined;
        latestCtaBlocksRef.current = [];
        pendingCommitActionsRef.current = [];
        sequenceStatusMessageIdRef.current = {};
        liveSequenceStatusMessageIdRef.current = undefined;
        lastAssistantMessageIdRef.current = undefined;
        lastAssistantIsStatusOnlyRef.current = false;

        void syncMetricsContextWithCurrentLocation();

        startStreaming({
          sessionId,
          sessionType,
          prompt: userMessage,
          pendingItems: buildPendingItems(sessionType),
          // Only 'scheduler_chat' requires targetDate on the request itself
          // (it looks up a previously generated schedule by date) — leave
          // other session types' requests unchanged.
          ...(sessionType === 'scheduler_chat' ? { targetDate: date } : {}),
          onMessage: (deltaText, textId) => {
            handleStreamingDelta(deltaText, sessionType, textId);
          },
          onWholeMessage: (message, textId) => {
            handleWholeMessage(message, sessionType, textId);
          },
          onSequenceStatus: handleSequenceStatus,
          onError: () => {
            addMessage({
              messageId: String(state.messages.length + 2),
              content: 'Sorry, I encountered an error during streaming. Please try again.',
              role: 'assistant',
              sentAt: new Date().toISOString(),
            });
            updateState({ loading: false, loadingStatusText: [] });
            activeSessionRef.current = null;
          },
          onStreamEnd: () => {
            finalizeStreamingResponse();
          },
          onResultOut: (result) => {
            handleFinalResult(result as any, sessionType, date, sessionId, userMessage);
          },
          onStatusText: handleStatusText,
          onQuestion: handleQuestionEvent,
          onCommitAction: handleCommitActionEvent,
          onNavigate: handleNavigateEvent,
          onEnrichmentPatch: handleEnrichmentPatchEvent,
        });

        trackEventMixpanel('assistant_chat_plan_entry', {
          planType: sessionType,
        });

        return sessionId;
      } catch (err) {
        if (err instanceof Error && err.name === 'CanceledError') return;
        throw err;
      }
    },
    [
      user,
      updateState,
      handleFinalResult,
      addAbortController,
      startStreaming,
      addMessage,
      handleStatusText,
      handleStreamingDelta,
      handleWholeMessage,
      handleSequenceStatus,
      finalizeStreamingResponse,
      handleQuestionEvent,
      handleCommitActionEvent,
      handleNavigateEvent,
      handleEnrichmentPatchEvent,
      buildPendingItems,
      state.messages.length,
      state.chatSessionId,
      syncMetricsContextWithCurrentLocation,
    ]
  );

  // ─── Resume a job that was still generating on reload ────────────────────

  useChatJobRecovery({
    isV4Enabled,
    getCallbacks: (context: ChatJobContext) => {
      // Mirror the reset handleGeneralChat/handlePlanChat do before starting
      // a live stream, so a resumed bubble behaves the same way.
      statusTextsRef.current = [];
      const prompt = context.prompt?.trim();
      const existing = messagesRef.current;
      let lastUserIndex = -1;
      if (prompt) {
        for (let i = existing.length - 1; i >= 0; i -= 1) {
          if (existing[i].role === 'user' && existing[i].content === prompt) {
            lastUserIndex = i;
            break;
          }
        }
      }
      const keptMessages = lastUserIndex === -1 ? existing : existing.slice(0, lastUserIndex + 1);
      const messages =
        prompt && lastUserIndex === -1
          ? [
              ...keptMessages,
              {
                messageId: `resume_user_${Date.now()}`,
                content: prompt,
                role: 'user' as const,
                sentAt: new Date().toISOString(),
              },
            ]
          : keptMessages;
      updateState({ messages, loading: true, loadingStatusText: [] });
      streamingDataRef.current = { summaryText: '', streamingMessageId: undefined, textId: undefined };
      sessionContextRef.current = {
        sessionType: context.sessionType,
        date: context.targetDate,
        sessionId: context.sessionId,
      };
      hasReceivedStreamingChunkRef.current = false;
      questionHandledFingerprintRef.current = null;
      ctaMessageIdRef.current = undefined;
      inlineScheduleRef.current = undefined;
      latestCtaBlocksRef.current = [];
      pendingCommitActionsRef.current = [];
      sequenceStatusMessageIdRef.current = {};
      liveSequenceStatusMessageIdRef.current = undefined;
      lastAssistantMessageIdRef.current = undefined;
      lastAssistantIsStatusOnlyRef.current = false;

      const effectiveSessionType = context.sessionType as StreamChatSessionType;

      return {
        onMessage: (deltaText, textId) => handleStreamingDelta(deltaText, effectiveSessionType, textId),
        onWholeMessage: (message, textId) => handleWholeMessage(message, effectiveSessionType, textId),
        onSequenceStatus: handleSequenceStatus,
        onStatusText: handleStatusText,
        onQuestion: handleQuestionEvent,
        onCommitAction: handleCommitActionEvent,
        onNavigate: handleNavigateEvent,
        onEnrichmentPatch: handleEnrichmentPatchEvent,
        onResultOut: (result) => {
          handleFinalResult(
            result,
            effectiveSessionType,
            context.targetDate,
            context.sessionId,
            context.prompt
          );
        },
      };
    },
    onDone: () => {
      finalizeStreamingResponse();
    },
    onError: () => {
      addMessage({
        messageId: `resume_error_${Date.now()}`,
        content: 'Sorry, I lost track of that response. Please try again.',
        role: 'assistant',
        sentAt: new Date().toISOString(),
      });
      updateState({ loading: false, loadingStatusText: [] });
    },
  });

  // ─── Public API ───────────────────────────────────────────────────────────

  const handlePlanEntry = useCallback(
    async (
      planType: ChatSessionType | 'freeText',
      specificTargetDate?: string,
      userMessage?: string
    ) => {
      if (!user) return;

      const newTargetDate = specificTargetDate ? dayjs(specificTargetDate) : dayjs();

      try {
        await handlePlanChat(
          planType as StreamChatSessionType,
          userMessage,
          newTargetDate.format('YYYY-MM-DD')
        );
      } catch {
        addMessage({
          messageId: String(state.messages.length + 2),
          content: 'Sorry, I encountered an error while planning. Please try again.',
          role: 'assistant',
          sentAt: new Date().toISOString(),
        });
      }
    },
    [user, state.messages.length, addMessage, handlePlanChat]
  );

  const sendMessage = useCallback(
    async (
      text: string,
      sessionType?: ChatSessionType | 'scheduler_chat',
      targetDate?: string
    ) => {
      const isExplanationFollowUp =
        state.messages.length === 1 && state.messages[0]?.messageId === 'expla_1';
      const explanationQuestion = isExplanationFollowUp ? state.messages[0]?.title : undefined;
      const messageToSend = explanationQuestion ? `${explanationQuestion}\n\n${text}` : text;

      const userMessage: AssistantChatMessage = {
        messageId: String(state.messages.length + 1),
        content: text,
        role: 'user',
        sentAt: new Date().toISOString(),
      };

      addMessage(userMessage);
      updateState({ isUserTurn: false });

      try {
        if (!sessionType || sessionType === 'scheduler' || sessionType === 'recommendation') {
          const previousAssistantMessage = [...state.messages]
            .reverse()
            .find((m) => m.role === 'assistant')?.content;

          await handleGeneralChat(messageToSend, { previousAssistantMessage });
        } else {
          await handlePlanChat(sessionType as StreamChatSessionType, messageToSend, targetDate);
        }
      } catch {
        addMessage({
          messageId: String(state.messages.length + 2),
          content: 'Sorry, I encountered an error. Please try again.',
          role: 'assistant',
          sentAt: new Date().toISOString(),
        });
        updateState({ loading: false, loadingStatusText: [] });
      } finally {
        updateState({ isUserTurn: true });
      }
    },
    [state.messages, updateState, addMessage, handleGeneralChat, handlePlanChat]
  );

  const sendQuestionAnswer = useCallback(
    async (questionMessageId: string, option: CTAOption) => {
      updateMessage(questionMessageId, { isAnswered: true });

      addMessage({
        messageId: String(Date.now()),
        content: option.label,
        role: 'user',
        sentAt: new Date().toISOString(),
      });

      updateState({ isUserTurn: false });

      statusTextsRef.current = [];
      updateState({ loading: true, loadingStatusText: [] });
      streamingDataRef.current = { summaryText: '', streamingMessageId: undefined, textId: undefined };
      hasReceivedStreamingChunkRef.current = false;
      questionHandledFingerprintRef.current = null;
      pendingCommitActionsRef.current = [];
      sequenceStatusMessageIdRef.current = {};
      liveSequenceStatusMessageIdRef.current = undefined;
      lastAssistantMessageIdRef.current = undefined;
      lastAssistantIsStatusOnlyRef.current = false;

      const { sessionType, date, sessionId } = sessionContextRef.current;
      const effectiveSessionType =
        sessionType === 'general' ? 'general' : (sessionType as StreamChatSessionType);

      void syncMetricsContextWithCurrentLocation();

      startStreaming({
        prompt: option.label,
        sessionType: effectiveSessionType,
        sessionId: sessionId ?? state.chatSessionId ?? undefined,
        targetDate: date,
        pendingItems: buildPendingItems(effectiveSessionType),
        hiddenContext: {
          conversation_response: true,
          selected_action: option.action,
          payload: option.payload,
        },
        onMessage: (deltaText, textId) =>
          handleStreamingDelta(deltaText, effectiveSessionType, textId),
        onWholeMessage: (message, textId) =>
          handleWholeMessage(message, effectiveSessionType, textId),
        onSequenceStatus: handleSequenceStatus,
        onError: () => {
          addMessage({
            messageId: String(Date.now()),
            content: 'Sorry, I encountered an error. Please try again.',
            role: 'assistant',
            sentAt: new Date().toISOString(),
          });
          updateState({ loading: false, loadingStatusText: [] });
          activeSessionRef.current = null;
        },
        onStreamEnd: () => {
          finalizeStreamingResponse();
          activeSessionRef.current = null;
        },
        onStatusText: handleStatusText,
        onQuestion: handleQuestionEvent,
        onCommitAction: handleCommitActionEvent,
        onEnrichmentPatch: handleEnrichmentPatchEvent,
        onResultOut: (result) => {
          handleFinalResult(
            result,
            effectiveSessionType,
            date,
            sessionId ?? state.chatSessionId ?? undefined,
            option.label
          );
        },
      });

      updateState({ isUserTurn: true });
    },
    [
      updateMessage,
      addMessage,
      updateState,
      startStreaming,
      handleStreamingDelta,
      handleWholeMessage,
      handleSequenceStatus,
      finalizeStreamingResponse,
      handleStatusText,
      handleQuestionEvent,
      handleCommitActionEvent,
      handleNavigateEvent,
      handleEnrichmentPatchEvent,
      handleFinalResult,
      buildPendingItems,
      syncMetricsContextWithCurrentLocation,
      state.chatSessionId,
    ]
  );

  const stopChat = useCallback(() => {
    stopStreaming();
    finalizeStreamingResponse();
    addMessage({
      messageId: `cancelled_${Date.now()}`,
      content: '',
      role: 'assistant',
      sentAt: new Date().toISOString(),
      cancelled: true,
    });
    updateState({ isUserTurn: true, loading: false, loadingStatusText: [] });
  }, [addMessage, finalizeStreamingResponse, stopStreaming, updateState]);

  return {
    handlePlanChat,
    handlePlanEntry,
    sendMessage,
    sendQuestionAnswer,
    isStreaming,
    streamingError: error,
    stopStreaming,
    stopChat,
  };
};
